import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Search, 
  ShoppingCart, 
  CreditCard, 
  Package, 
  CheckCircle, 
  Shield, 
  Heart,
  ArrowRight,
  Sparkles,
  Gift,
  MessageSquare,
  Eye,
  Truck,
  Clock,
  ChevronDown,
  ChevronRight,
  Lock,
  BadgeCheck,
  ThumbsUp,
  FileText,
  Send,
  Hammer,
  Camera,
  Star,
  AlertCircle,
  HelpCircle
} from 'lucide-react';

// Steps for buying from catalog
const CATALOG_STEPS = [
  {
    number: '01',
    title: 'Esplora e Cerca',
    description: 'Naviga tra le categorie o usa la barra di ricerca. Filtra per prezzo, materiale, stile o artigiano per trovare esattamente ciò che cerchi.',
    icon: Search,
    color: 'bg-blue-500',
    tips: ['Usa i filtri per restringere la ricerca', 'Salva i preferiti con il cuoricino', 'Leggi le recensioni degli altri clienti']
  },
  {
    number: '02',
    title: 'Scegli il Prodotto',
    description: 'Esamina le foto in alta risoluzione, leggi la descrizione dettagliata e scopri la storia dell\'artigiano. Se hai dubbi, contattalo direttamente.',
    icon: Eye,
    color: 'bg-purple-500',
    tips: ['Verifica le dimensioni esatte', 'Controlla i tempi di realizzazione', 'Chiedi personalizzazioni se disponibili']
  },
  {
    number: '03',
    title: 'Aggiungi al Carrello',
    description: 'Seleziona eventuali varianti (colore, taglia, personalizzazione) e aggiungi al carrello. Puoi acquistare da più artigiani in un unico ordine.',
    icon: ShoppingCart,
    color: 'bg-green-500',
    tips: ['Verifica la disponibilità', 'Applica codici sconto se ne hai', 'La spedizione si calcola automaticamente']
  },
  {
    number: '04',
    title: 'Checkout Sicuro',
    description: 'Inserisci l\'indirizzo di spedizione e procedi al pagamento con Stripe. Il tuo pagamento è protetto fino alla conferma di ricezione.',
    icon: CreditCard,
    color: 'bg-amber-500',
    tips: ['Pagamenti con carta o altri metodi', 'Connessione crittografata SSL', 'Ricevi conferma via email']
  },
  {
    number: '05',
    title: 'Ricevi e Conferma',
    description: 'Segui la spedizione con il tracking. Quando ricevi il prodotto, conferma la ricezione e solo allora l\'artigiano riceverà il pagamento.',
    icon: Package,
    color: 'bg-teal-500',
    tips: ['Tracking in tempo reale', 'Conferma entro 7 giorni', 'Lascia una recensione per aiutare altri']
  }
];

// Steps for custom requests
const CUSTOM_STEPS = [
  {
    number: '01',
    title: 'Crea la Tua Richiesta',
    description: 'Descrivi in dettaglio cosa desideri: materiali preferiti, dimensioni, colori, utilizzo previsto. Più dettagli fornisci, migliori saranno le proposte.',
    icon: FileText,
    color: 'bg-rose-500',
    details: 'Puoi allegare immagini di ispirazione, specificare il budget e indicare la data entro cui ti serve il prodotto.'
  },
  {
    number: '02',
    title: 'Ricevi Proposte',
    description: 'Gli artigiani interessati ti invieranno le loro proposte complete di prezzo, tempi di realizzazione e dettagli tecnici. Nessun impegno da parte tua.',
    icon: Send,
    color: 'bg-indigo-500',
    details: 'Puoi ricevere più proposte e confrontarle con calma. Chatta con gli artigiani per chiarire ogni dubbio.'
  },
  {
    number: '03',
    title: 'Scegli e Conferma',
    description: 'Valuta le proposte ricevute, confronta prezzi e portfolio degli artigiani. Quando trovi quella giusta, accettala e procedi al pagamento sicuro.',
    icon: ThumbsUp,
    color: 'bg-emerald-500',
    details: 'Il pagamento viene trattenuto da Stripe fino al completamento. L\'artigiano può iniziare a lavorare in sicurezza.'
  },
  {
    number: '04',
    title: 'Segui la Creazione',
    description: 'L\'artigiano ti terrà aggiornato con foto e messaggi durante tutto il processo di realizzazione. Puoi vedere il tuo pezzo unico prendere forma.',
    icon: Camera,
    color: 'bg-orange-500',
    details: 'Riceverai notifiche ad ogni aggiornamento. Le foto di lavorazione vengono salvate e condivise con te.'
  },
  {
    number: '05',
    title: 'Ricevi il Capolavoro',
    description: 'Quando il prodotto è pronto, l\'artigiano lo spedisce. Conferma la ricezione e rilascia il pagamento. Il tuo pezzo unico è finalmente tuo!',
    icon: Gift,
    color: 'bg-pink-500',
    details: 'L\'artigiano riceve il compenso solo dopo la tua conferma. Massima protezione per entrambe le parti.'
  }
];

// Payment protection features
const PROTECTION_FEATURES = [
  {
    icon: Shield,
    title: 'Pagamento Trattenuto',
    description: 'Il tuo pagamento viene trattenuto da Stripe in modo sicuro. L\'artigiano lo riceve solo quando confermi di aver ricevuto il prodotto.'
  },
  {
    icon: Lock,
    title: 'Crittografia SSL',
    description: 'Tutti i dati sono protetti con crittografia SSL a 256 bit. I tuoi dati di pagamento non vengono mai memorizzati sui nostri server.'
  },
  {
    icon: BadgeCheck,
    title: 'Artigiani Verificati',
    description: 'Ogni artigiano viene verificato prima di poter vendere. Controlliamo identità, partita IVA e qualità dei prodotti.'
  },
  {
    icon: MessageSquare,
    title: 'Supporto Dedicato',
    description: 'In caso di problemi, il nostro team è a disposizione per mediare e trovare una soluzione equa per tutti.'
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: 'Quando viene addebitato il pagamento?',
    answer: 'Il pagamento viene addebitato immediatamente al momento del checkout, ma viene trattenuto da Stripe in modo sicuro. L\'artigiano riceve il compenso solo quando confermi di aver ricevuto il prodotto in buone condizioni.'
  },
  {
    question: 'Cosa succede se il prodotto non mi piace o è difettoso?',
    answer: 'Se il prodotto non corrisponde alla descrizione o presenta difetti, puoi aprire una contestazione prima di confermare la ricezione. Il nostro team valuterà il caso e, se necessario, procederà al rimborso totale o parziale.'
  },
  {
    question: 'Posso annullare un ordine dopo il pagamento?',
    answer: 'Per i prodotti a catalogo già pronti, puoi richiedere l\'annullamento prima della spedizione. Per i prodotti personalizzati o su misura, una volta iniziata la lavorazione non è possibile annullare (il diritto di recesso non si applica ai prodotti personalizzati).'
  },
  {
    question: 'Come funziona la spedizione?',
    answer: 'Ogni artigiano gestisce le proprie spedizioni. I costi e i tempi variano in base alla località e al tipo di prodotto. Tutti gli ordini sono tracciabili e riceverai il codice tracking via email e nella tua dashboard.'
  },
  {
    question: 'Posso acquistare da più artigiani insieme?',
    answer: 'Sì! Puoi aggiungere prodotti di diversi artigiani al carrello e pagare tutto insieme. Ogni artigiano spedirà separatamente la propria parte dell\'ordine, e i costi di spedizione sono calcolati per ciascuno.'
  },
  {
    question: 'Come faccio a sapere se un artigiano è affidabile?',
    answer: 'Tutti gli artigiani su Rarifex sono verificati. Puoi vedere le recensioni dei clienti precedenti, il punteggio medio, il numero di ordini completati e da quanto tempo sono sulla piattaforma. Gli artigiani con partita IVA sono identificati con un badge.'
  },
  {
    question: 'Quanto tempo ho per confermare la ricezione?',
    answer: 'Hai 7 giorni dalla consegna per confermare la ricezione o aprire una contestazione. Se non fai nulla entro questo periodo, il sistema conferma automaticamente la ricezione e rilascia il pagamento all\'artigiano.'
  },
  {
    question: 'Posso chiedere modifiche durante la realizzazione di un prodotto su misura?',
    answer: 'Dipende dalla fase di lavorazione e dal tipo di modifica. Contatta l\'artigiano via chat per discutere eventuali variazioni. Modifiche sostanziali potrebbero comportare costi aggiuntivi o allungamento dei tempi.'
  }
];

export default function HowToBuyPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('catalog');

  return (
    <>
      <Helmet>
        <title>Come Acquistare su Rarifex | Guida all'Acquisto Sicuro | Artigianato Italiano</title>
        <meta name="description" content="Scopri come acquistare in sicurezza su Rarifex: guida completa all'acquisto di prodotti artigianali Made in Italy, checkout protetto con Stripe, e garanzia di pagamento all'artigiano solo a prodotto ricevuto." />
        <meta name="keywords" content="come acquistare rarifex, acquisto sicuro artigianato, pagamento protetto stripe, comprare made in italy, guida acquisto online, prodotti artigianali italiani" />
        <link rel="canonical" href="https://www.rarifex.com/come-acquistare" />
        <meta property="og:title" content="Come Acquistare su Rarifex | Guida all'Acquisto Sicuro" />
        <meta property="og:description" content="Guida completa all'acquisto sicuro di prodotti artigianali Made in Italy su Rarifex. Pagamento protetto e garanzia totale." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/come-acquistare" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-shop-primary/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-shop-primary/10 text-shop-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <ShoppingCart className="w-4 h-4" />
                Guida all'Acquisto
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Acquista in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-shop-primary to-shop-primary-dark">
                  totale sicurezza
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Scopri come acquistare prodotti artigianali unici con la massima protezione. 
                <strong className="text-slate-800"> L'artigiano riceve il pagamento solo quando confermi la ricezione.</strong>
              </p>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-10 mt-10">
                <div className="flex items-center gap-2 text-slate-700">
                  <Shield className="w-5 h-5 text-shop-primary" />
                  <span className="text-sm font-medium">Pagamento Protetto</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Lock className="w-5 h-5 text-shop-primary" />
                  <span className="text-sm font-medium">Checkout Stripe SSL</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <BadgeCheck className="w-5 h-5 text-shop-primary" />
                  <span className="text-sm font-medium">Artigiani Verificati</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TAB SELECTOR */}
        <section className="container mx-auto px-4 -mt-4 mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-2 flex gap-2">
              <button
                onClick={() => setActiveTab('catalog')}
                className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'catalog' 
                    ? 'bg-shop-primary text-white shadow-md' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Acquisto dal Catalogo</span>
              </button>
              <button
                onClick={() => setActiveTab('custom')}
                className={`flex-1 py-4 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'custom' 
                    ? 'bg-shop-accent text-white shadow-md' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Gift className="w-5 h-5" />
                <span>Richiesta Personalizzata</span>
              </button>
            </div>
          </div>
        </section>

        {/* CATALOG PURCHASE STEPS */}
        {activeTab === 'catalog' && (
          <section className="py-12 md:py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    Acquistare dal Catalogo
                  </h2>
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                    Prodotti già pronti, realizzati con cura dagli artigiani. 
                    Spediti direttamente a casa tua.
                  </p>
                </div>

                <div className="space-y-6">
                  {CATALOG_STEPS.map((step, index) => (
                    <Card key={index} className="p-6 md:p-8 bg-white border-slate-200 hover:border-shop-primary/30 hover:shadow-lg transition-all">
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Step number and icon */}
                        <div className="flex md:flex-col items-center gap-4 md:gap-2">
                          <div className={`w-14 h-14 ${step.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                            <step.icon className="w-7 h-7 text-white" />
                          </div>
                          <span className="text-2xl font-bold text-slate-300">{step.number}</span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                          <p className="text-slate-600 mb-4">{step.description}</p>
                          
                          {/* Tips */}
                          <div className="bg-slate-50 rounded-xl p-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Suggerimenti</p>
                            <ul className="space-y-1">
                              {step.tips.map((tip, tipIndex) => (
                                <li key={tipIndex} className="flex items-start gap-2 text-sm text-slate-600">
                                  <ChevronRight className="w-4 h-4 text-shop-primary flex-shrink-0 mt-0.5" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Connector line (hidden on mobile) */}
                        {index < CATALOG_STEPS.length - 1 && (
                          <div className="hidden md:block absolute left-[3.25rem] top-full w-0.5 h-6 bg-slate-200" />
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="mt-10 text-center">
                  <Link to="/products">
                    <Button size="lg" className="bg-shop-primary hover:bg-shop-primary-dark text-white px-8">
                      Esplora il Catalogo
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CUSTOM REQUEST STEPS */}
        {activeTab === 'custom' && (
          <section className="py-12 md:py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    Richiedere una Creazione Personalizzata
                  </h2>
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                    Hai in mente qualcosa di unico? Descrivi la tua idea e lascia che gli artigiani 
                    ti stupiscano con le loro proposte.
                  </p>
                </div>

                <div className="space-y-6">
                  {CUSTOM_STEPS.map((step, index) => (
                    <Card key={index} className="p-6 md:p-8 bg-gradient-to-r from-amber-50/50 to-rose-50/50 border-amber-200/50 hover:shadow-lg transition-all">
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Step number and icon */}
                        <div className="flex md:flex-col items-center gap-4 md:gap-2">
                          <div className={`w-14 h-14 ${step.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                            <step.icon className="w-7 h-7 text-white" />
                          </div>
                          <span className="text-2xl font-bold text-amber-300">{step.number}</span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                          <p className="text-slate-600 mb-4">{step.description}</p>
                          
                          {/* Details */}
                          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-amber-100">
                            <div className="flex items-start gap-2">
                              <Sparkles className="w-4 h-4 text-shop-accent flex-shrink-0 mt-0.5" />
                              <p className="text-sm text-slate-600">{step.details}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="mt-10 text-center">
                  <Link to="/regalo-personalizzato">
                    <Button size="lg" className="bg-shop-accent hover:bg-[#B8860B] text-white px-8">
                      Crea la Tua Richiesta
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* PAYMENT PROTECTION SECTION */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-shop-primary/20 text-[#00CED1] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  Protezione Garantita
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Il tuo pagamento è sempre al sicuro
                </h2>
                <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                  Utilizziamo Stripe, il sistema di pagamento più sicuro al mondo, con un ulteriore livello di protezione: 
                  <strong className="text-white"> l'artigiano riceve il compenso solo dopo che confermi di aver ricevuto il prodotto</strong>.
                </p>
              </div>

              {/* Protection flow diagram */}
              <div className="bg-slate-800/50 rounded-3xl p-8 md:p-12 mb-12">
                <div className="grid md:grid-cols-4 gap-6 md:gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-8 h-8 text-blue-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">1. Paghi</h4>
                    <p className="text-sm text-slate-400">Checkout sicuro con Stripe</p>
                  </div>
                  
                  <div className="hidden md:flex items-center justify-center">
                    <ArrowRight className="w-8 h-8 text-slate-600" />
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-8 h-8 text-amber-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">2. Stripe Trattiene</h4>
                    <p className="text-sm text-slate-400">Il pagamento è custodito in sicurezza</p>
                  </div>
                  
                  <div className="hidden md:flex items-center justify-center">
                    <ArrowRight className="w-8 h-8 text-slate-600" />
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Hammer className="w-8 h-8 text-purple-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">3. L'Artigiano Lavora</h4>
                    <p className="text-sm text-slate-400">Realizza e spedisce il prodotto</p>
                  </div>
                  
                  <div className="hidden md:flex items-center justify-center">
                    <ArrowRight className="w-8 h-8 text-slate-600" />
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">4. Confermi</h4>
                    <p className="text-sm text-slate-400">Solo ora l'artigiano riceve il compenso</p>
                  </div>
                </div>
              </div>

              {/* Protection features grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {PROTECTION_FEATURES.map((feature, index) => (
                  <div 
                    key={index}
                    className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-shop-primary/50 transition-all"
                  >
                    <div className="w-12 h-12 bg-shop-primary/20 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#00CED1]" />
                    </div>
                    <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-slate-400">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* IMPORTANT NOTICE */}
        <section className="py-12 md:py-16 bg-amber-50 border-y border-amber-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Nota sui Prodotti Personalizzati
                  </h3>
                  <p className="text-slate-700 mb-4">
                    Per i prodotti realizzati su misura o personalizzati secondo le tue specifiche, 
                    il <strong>diritto di recesso non è applicabile</strong> ai sensi dell'Art. 59 del Codice del Consumo. 
                    Questo perché si tratta di creazioni uniche, realizzate appositamente per te.
                  </p>
                  <p className="text-slate-600 text-sm">
                    Prima di procedere all'acquisto di un prodotto personalizzato, ti verrà chiesto di confermare 
                    di aver compreso questa condizione. In caso di difetti o non conformità, rimane comunque 
                    attiva la garanzia legale.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <HelpCircle className="w-4 h-4" />
                  Domande Frequenti
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Hai ancora dubbi?
                </h2>
                <p className="text-lg text-slate-600">
                  Ecco le risposte alle domande più comuni sull'acquisto su Rarifex.
                </p>
              </div>

              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-slate-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-shop-primary to-shop-primary-dark">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Pronto per il tuo primo acquisto?
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                Scopri migliaia di creazioni artigianali Made in Italy e acquista in totale sicurezza.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-white text-shop-primary hover:bg-slate-100">
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Esplora il Catalogo
                  </Button>
                </Link>
                <Link to="/regalo-personalizzato">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Gift className="w-5 h-5 mr-2" />
                    Richiedi su Misura
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
